import { memo } from 'react';
import { useTranslation, useIsRTL } from '@spcc/core';
import { Link } from '@spcc/core';
import {
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  ShoppingCart,
  Eye,
  RefreshCw,
  Trash2,
  MoreHorizontal,
  Package,
  Truck,
  Factory,
  Badge,
  TableCell,
  TableRow,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TenantCell,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@spcc/uikit';
import type { PackagingType } from '../../../../domain/types';
import type { SalesOrderRowProps } from '../types';
import { statusConfig, getProductColorStyles } from '../constants';
import { calculateUtilization, getSalesOrderLineLabel } from '../utils';

const PRODUCT_EXCLUDED_WORDS = new Set(['paper', 'bag', 'plastic', 'bulk'])

function abbreviateProductName(name: string): string {
  if (!name || name === '—') return name
  const words = name.trim().split(/\s+/)
  const significant = words.filter(w => !PRODUCT_EXCLUDED_WORDS.has(w.toLowerCase()))
  if (significant.length === 0) return name
  return significant.map(w => w.charAt(0).toUpperCase()).join('')
}

function getSalesOrderRowProductDisplayLabel(
  label: string,
  isArabic: boolean,
): string {
  if (isArabic) return label
  if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(label)) {
    return label
  }
  return abbreviateProductName(label)
}

function SalesOrderRowComponent({
  salesOrder,
  isSelected,
  showTenantColumn,
  onRowClick,
  onRowDoubleClick,
  onView,
  onEdit,
  onReorder,
  onCancel,
}: SalesOrderRowProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useIsRTL();
  const isArabic = i18n.language === 'ar'; // For Arabic-specific content (translations)
  const rawStatus = String((salesOrder as { status?: unknown }).status ?? '');
  const normalizedStatus = rawStatus === 'cancelled' ? 'canceled' : rawStatus;
  const statusKey = (
    normalizedStatus in statusConfig ? normalizedStatus : 'draft'
  ) as keyof typeof statusConfig;
  const config = statusConfig[statusKey];
  const utilization = calculateUtilization(salesOrder);
  const ordersCount = salesOrder.ordersCount ?? { total: 0, pending: 0 };
  const ArrowIcon = isRTL ? ChevronLeft : ChevronRight;
  const StatusIcon = config.icon;
  const productLines = ((salesOrder as any).products ??
    (salesOrder as any).lines ??
    []) as any[];

  return (
    <TableRow
      className={cn(
        'border-border/30 cursor-pointer group transition-colors duration-150',
        isSelected
          ? 'bg-primary/8 hover:bg-primary/12 ring-1 ring-inset ring-primary/20'
          : 'hover:bg-muted/50',
      )}
      onClick={() => onRowClick(salesOrder)}
      onDoubleClick={() => onRowDoubleClick(salesOrder)}
      data-testid={`sales-orders-row-${salesOrder.id}`}
    >
      {/* Sales Order */}
      <TableCell className="px-5 py-4">
        <div className="flex flex-col gap-0.5 items-start">
          <span className="font-semibold text-foreground text-sm">
            {salesOrder.orderNumber}
          </span>
          <span className="text-xs text-muted-foreground truncate max-w-[200px]">
            {isArabic ? salesOrder.name.ar : salesOrder.name.en}
          </span>
        </div>
      </TableCell>

      {/* Tenant (Admin Only) */}
      {showTenantColumn && (
        <TableCell className="px-5 py-4">
          <TenantCell tenant={salesOrder.tenant} />
        </TableCell>
      )}

      {/* Factory */}
      <TableCell className="px-5 py-4">
        <div className={cn('flex items-center gap-2')}>
          <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <Factory className="w-4 h-4 text-slate-500" />
          </div>
          <span className="text-sm text-foreground font-medium truncate max-w-[120px]">
            {salesOrder.factoryName || '—'}
          </span>
        </div>
      </TableCell>

      {/* Products */}
      <TableCell className="px-5 py-4">
        <div className="flex items-center gap-1.5 flex-wrap">
          {productLines.slice(0, 3).map((line, idx) => {
            const label = getSalesOrderLineLabel(line as any, isArabic) || '—';
            const displayLabel = getSalesOrderRowProductDisplayLabel(label, isArabic);
            const packaging =
              ((line as any).packaging as PackagingType) || 'bags';
            const colors = getProductColorStyles(label);
            const PackageIcon = packaging === 'bulk' ? Truck : Package;

            return (
              <Tooltip key={idx}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold shadow-sm cursor-default',
                    )}
                    style={{ backgroundColor: colors.bg, color: colors.text }}
                  >
                    <span
                      className="w-2 h-2 rounded-full ring-1 ring-white/50"
                      style={{ backgroundColor: colors.dot }}
                    />
                    <span>{displayLabel}</span>
                    <PackageIcon className="w-3 h-3 opacity-70" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>{label}</TooltipContent>
              </Tooltip>
            );
          })}
          {productLines.length > 3 && (
            <span className="text-xs text-muted-foreground font-medium px-1.5">
              +{productLines.length - 3}
            </span>
          )}
        </div>
      </TableCell>

      {/* Consumption - Progress bar with percentage */}
      <TableCell className="px-5 py-4">
        <div className="flex items-center gap-3 min-w-[160px]">
          <div className="flex-1 h-3 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden shadow-inner">
            <div
              className={cn(
                'h-full rounded-full transition-[width] duration-300 shadow-sm',
                utilization >= 90
                  ? 'bg-gradient-to-r from-red-500 to-red-400'
                  : utilization >= 70
                    ? 'bg-gradient-to-r from-amber-500 to-amber-400'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-400',
              )}
              style={{
                width: `${Math.min(utilization, 100)}%`,
                background:
                  utilization >= 90
                    ? 'linear-gradient(to right, rgb(239 68 68), rgb(248 113 113))'
                    : utilization >= 70
                      ? 'linear-gradient(to right, rgb(245 158 11), rgb(251 191 36))'
                      : 'linear-gradient(to right, rgb(16 185 129), rgb(52 211 153))',
              }}
            />
          </div>
          <div
            className={cn(
              'min-w-[50px] px-2.5 py-1 rounded-lg text-xs font-bold text-center whitespace-nowrap',
              utilization >= 90
                ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'
                : utilization >= 70
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
            )}
          >
            {utilization}%
          </div>
        </div>
      </TableCell>

      {/* Orders */}
      <TableCell className="px-5 py-4">
        {ordersCount.total > 0 ? (
          <div className="inline-flex items-center gap-2 group/orders">
            <span className="text-sm text-foreground">{ordersCount.total}</span>
            <Link
              to="/orders"
              search={{ salesOrderId: salesOrder.id }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="opacity-0 group-hover/orders:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>

      {/* Status */}
      <TableCell
        className="px-5 py-4"
        data-testid={`sales-orders-cell-status-${salesOrder.id}`}
      >
        <Badge
          className={cn(
            config.bg,
            config.text,
            'border-0 font-semibold px-3 py-1 shadow-sm flex items-center gap-1.5',
          )}
        >
          <StatusIcon className="w-3.5 h-3.5" />
          <span
            className={cn(
              'w-2 h-2 rounded-full animate-pulse',
              config.dotColor,
            )}
          />
          {t(`salesOrders.${statusKey}`)}
        </Badge>
      </TableCell>

      {/* Actions */}
      <TableCell className="px-5 py-4 text-end">
        <div className={cn('flex items-center justify-end gap-2')}>
          <DropdownMenu>
            <DropdownMenuTrigger
              asChild
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl hover:bg-muted"
                data-testid={`sales-orders-actions-${salesOrder.id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onView(salesOrder);
                }}
                data-testid={`sales-orders-action-view-${salesOrder.id}`}
              >
                <Eye className="w-4 h-4 me-2" />
                {t('common.view')}
              </DropdownMenuItem>
              {onReorder && (
                <DropdownMenuItem
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onReorder(salesOrder);
                  }}
                  data-testid={`sales-orders-action-reorder-${salesOrder.id}`}
                >
                  <RefreshCw className="w-4 h-4 me-2" />
                  {t('salesOrders.reorder')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link
                  to="/orders"
                  search={{ salesOrderId: salesOrder.id }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex items-center"
                  data-testid={`sales-orders-action-orders-${salesOrder.id}`}
                >
                  <ShoppingCart className="w-4 h-4 me-2" />
                  {t('salesOrders.viewOrders')}
                  {ordersCount.total > 0 && (
                    <Badge className="ms-auto bg-blue-500 text-white border-0 text-[10px] px-1.5 py-0 h-4 min-w-[18px] flex items-center justify-center">
                      {ordersCount.total}
                    </Badge>
                  )}
                </Link>
              </DropdownMenuItem>
              {onCancel && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onCancel(salesOrder);
                    }}
                    variant="destructive"
                    data-testid={`sales-orders-action-cancel-${salesOrder.id}`}
                  >
                    <Trash2 className="w-4 h-4 me-2" />
                    {t('salesOrders.cancel')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

export const SalesOrderRow = memo(SalesOrderRowComponent, (prev, next) => {
  return (
    prev.salesOrder.id === next.salesOrder.id &&
    prev.isSelected === next.isSelected &&
    prev.salesOrder.status === next.salesOrder.status &&
    prev.showTenantColumn === next.showTenantColumn
  );
});
